// Cambio PRC-049: en la pestaña «Archivados» de Cadencia, los episodios que se
// archivaron al cerrar su seguimiento («Situación explicada» o «No relevante»),
// con motivo, referencia sustitutiva y conclusión. Tabla densa (diseño 33, 12).
import React from 'react';
import { Box, ButtonBase } from '@mui/material';
import type { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query';
import { J } from '../../../pricingJourney/journeyTokens';
import { formatInteger } from '../../../pricingJourney/formatNumber';
import type { CadenceArchivedFollowUpEpisodeItem, CadenceArchivedFollowUpEpisodesResponse } from '../../types/cadenceAttention';
import { formatDayDate } from './cadenceManagementTexts';

const thSx = { textAlign: 'left', fontSize: 11, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#64748b', p: '6px 12px', borderBottom: '1px solid rgba(148,163,184,.16)', whiteSpace: 'nowrap' } as const;
const tdSx = { p: '6px 12px', height: 38, lineHeight: 1.25, borderBottom: '1px solid rgba(148,163,184,.08)', whiteSpace: 'nowrap', fontSize: 13, verticalAlign: 'middle' } as const;
const clipSx = (max: number) => ({ maxWidth: max, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) as const;

export const archivedMotive = (item: CadenceArchivedFollowUpEpisodeItem): string => {
  const reason = item.follow_up.resolution_type
    || (item.follow_up.close_reason === 'not_relevant' ? 'Sin relevancia comercial' : 'Situación explicada');
  return item.follow_up.related_product_id ? `${reason} · ${item.follow_up.related_product_id}` : reason;
};

interface Props {
  query: UseInfiniteQueryResult<InfiniteData<CadenceArchivedFollowUpEpisodesResponse, unknown>, Error>;
  onOpenFollowUp: (followUpId: string) => void;
}

const ArchivedFollowUpEpisodesTable: React.FC<Props> = ({ query, onOpenFollowUp }) => {
  const items = (query.data?.pages ?? []).flatMap((p) => p.items);
  const total = query.data?.pages?.[0]?.summary?.archived_follow_up_count ?? 0;
  if (query.isError) {
    return <Box sx={{ fontSize: 13, color: J.dangerText }}>No se pudieron cargar los avisos archivados desde un seguimiento.</Box>;
  }
  if (items.length === 0) return null;
  return (
    <Box sx={{ fontFamily: J.font, color: J.text }}>
      <Box sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: J.skyText, mb: '6px' }}>
        Archivados al cerrar su seguimiento · {formatInteger(total)}
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Cliente · referencia', 'Última compra', 'Motivo', 'Conclusión', 'Cerrado', ''].map((h) => (
                <Box component="th" key={h || 'accion'} sx={thSx}>{h}</Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const motive = archivedMotive(item);
              return (
                <Box component="tr" key={`${item.follow_up.id}-${item.first_outside_p90_date}`} sx={{ '&:hover td': { bgcolor: 'rgba(56,189,248,.04)' } }}>
                  <Box component="td" sx={tdSx} title={`${item.customer_label} · ${item.product_id} · ${item.product_label}`}>
                    <Box sx={clipSx(360)}>
                      <Box component="span" sx={{ fontWeight: 800 }}>{item.customer_label}</Box>
                      <Box component="span" sx={{ ml: 1, fontSize: 12, color: J.muted }}>
                        {item.product_id}{item.product_label && item.product_label !== item.product_id ? ` · ${item.product_label}` : ''}
                      </Box>
                    </Box>
                  </Box>
                  <Box component="td" sx={tdSx}>{formatDayDate(item.facts?.last_purchase_date ?? null)}</Box>
                  <Box component="td" sx={tdSx} title={motive}><Box sx={clipSx(260)}>{motive}</Box></Box>
                  <Box component="td" sx={tdSx} title={item.follow_up.conclusion ?? ''}><Box sx={clipSx(360)}>{item.follow_up.conclusion || '—'}</Box></Box>
                  <Box component="td" sx={tdSx}>{formatDayDate(item.follow_up.closed_at)}</Box>
                  <Box component="td" sx={{ ...tdSx, textAlign: 'right', width: 1 }}>
                    <ButtonBase onClick={() => onOpenFollowUp(item.follow_up.id)} sx={{ height: 26, px: '10px', fontSize: 12, fontWeight: 700, borderRadius: '8px', border: '1px solid #334155', color: J.text, fontFamily: J.font }}>
                      Ver seguimiento
                    </ButtonBase>
                  </Box>
                </Box>
              );
            })}
          </tbody>
        </Box>
      </Box>
      {query.hasNextPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '8px' }}>
          <ButtonBase disabled={query.isFetchingNextPage} onClick={() => { void query.fetchNextPage(); }} sx={{ color: J.skyText, fontSize: 13, fontWeight: 700, fontFamily: J.font }}>
            {query.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
};

export default ArchivedFollowUpEpisodesTable;
